import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../hooks/useTranslation'

const ROUTE_LABELS: Record<string, string> = {
  '/': 'nav.dashboard',
  '/projects': 'nav.projects',
  '/projects/new': 'Add Project',
  '/researchers': 'nav.researchers',
  '/pipeline': 'nav.pipeline',
  '/settings': 'nav.settings',
}

export function TopBar() {
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const routeKey = ROUTE_LABELS[location.pathname] || 'nav.dashboard'
  const pageLabel = routeKey.startsWith('nav.') ? t(routeKey) : routeKey

  const handleLogout = async () => {
    setMenuOpen(false)
    await logout()
    navigate('/login')
  }

  const initials = user?.name
    ? user.name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() || '').join('')
    : user?.email.charAt(0).toUpperCase() || '?'

  return (
    <header className="h-12 flex-shrink-0 bg-[#12121e] border-b border-border-subtle flex items-center px-6">
      {/* Page title */}
      <div className="flex flex-col justify-center items-start">
        <span className="text-[14px] font-semibold text-text-primary tracking-wide">
          {pageLabel}
        </span>
        <div className="h-[2px] w-full bg-primary-red rounded-full mt-0.5" />
      </div>

      {/* User + Add Project button */}
      {user && (
        <div className="ml-auto flex items-center gap-4">
          {location.pathname.startsWith('/projects') && location.pathname !== '/projects/new' && (
            <button
              onClick={() => navigate('/projects/new')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-red text-white hover:bg-primary-red/90 transition-colors text-[12px] font-medium"
            >
              <span>+</span>
              <span>Add Project</span>
            </button>
          )}

          {/* Avatar with dropdown */}
          <div className="relative" ref={menuRef}>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="text-[12px] font-medium text-text-primary">{user.name || user.email}</div>
              </div>
              <button
                onClick={() => setMenuOpen(prev => !prev)}
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-red/50 focus:ring-offset-1 focus:ring-offset-[#12121e]"
                style={{
                  backgroundImage: user.picture_url ? `url('${user.picture_url}')` : undefined,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  backgroundColor: !user.picture_url ? undefined : 'transparent',
                }}
                title={user.name || user.email}
              >
                {!user.picture_url && (
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-primary-red to-[#F97316] flex items-center justify-center">
                    <span className="text-[12px] font-semibold text-white">{initials}</span>
                  </div>
                )}
              </button>
            </div>

            {/* Dropdown menu */}
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-48 bg-[#1e1e38] border border-border-subtle rounded-lg shadow-lg overflow-hidden z-50">
                <div className="px-3 py-2.5 border-b border-border-subtle">
                  <div className="text-[12px] font-medium text-text-primary truncate">{user.name || user.email}</div>
                  <div className="text-[10px] text-text-tertiary truncate">{user.email}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-[12px] text-text-secondary hover:bg-[#2a2a44] hover:text-text-primary transition-colors flex items-center gap-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
